import React, { useState, useEffect } from 'react';
import { Bell, Mail, MessageSquare, Monitor, Loader } from 'lucide-react';
import { httpClient } from '../../services/httpClient';
import { countryCodes, flagEmoji } from '../../utils/countryCodes';

interface NotificationPreferences {
  emailEnabled: boolean;
  inAppEnabled: boolean;
  smsEnabled: boolean;
  analysisEmail: boolean;
  analysisSms: boolean;
  analysisInApp: boolean;
  smsPhoneNumber: string;
}

export const NotificationPreferencesSection: React.FC = () => {
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [diallingCode, setDiallingCode] = useState('+44');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  /** Split a stored full phone number into dialling code + local part */
  const parseStoredPhone = (full: string) => {
    if (!full) return;
    // Try to match against known codes (longest first to avoid +1 matching +1xxx numbers)
    const sorted = [...countryCodes].sort((a, b) => b.code.length - a.code.length);
    for (const c of sorted) {
      if (full.startsWith(c.code)) {
        setDiallingCode(c.code);
        setPhoneInput(full.slice(c.code.length).trim());
        return;
      }
    }
    // Fallback — put entire value in input
    setPhoneInput(full);
  };

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const data = await httpClient.get<NotificationPreferences>('/notifications/preferences');
      setPrefs(data);
      parseStoredPhone(data.smsPhoneNumber || '');
    } catch {
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!prefs) return;
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    setSaving(true);
    setError('');
    try {
      await httpClient.put('/notifications/preferences', { [key]: value });
    } catch {
      // Revert on failure
      setPrefs(prefs);
      setError('Failed to save preference');
    } finally {
      setSaving(false);
    }
  };

  const getFullPhone = () => {
    const local = phoneInput.replace(/^0+/, '').trim();
    return `${diallingCode}${local}`;
  };

  const savePhone = async () => {
    if (!prefs) return;
    const fullNumber = getFullPhone();
    setPhoneSaving(true);
    setError('');
    try {
      await httpClient.put('/notifications/preferences', { smsPhoneNumber: fullNumber });
      setPrefs({ ...prefs, smsPhoneNumber: fullNumber });
    } catch {
      setError('Failed to save phone number');
    } finally {
      setPhoneSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card dark:bg-medical-800 dark:border-medical-700">
        <div className="flex items-center justify-center py-8">
          <Loader className="h-6 w-6 animate-spin text-primary-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="card dark:bg-medical-800 dark:border-medical-700">
      <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-4 flex items-center space-x-2">
        <Bell className="h-5 w-5" />
        <span>Notification Preferences</span>
      </h3>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <p className="text-sm text-medical-600 dark:text-medical-400 mb-4">
        Choose how you want to be notified when document analysis completes. This is useful for large bundles that take time to process.
      </p>

      {prefs && (
        <div className="space-y-3">
          {/* Analysis Complete — In-App */}
          <ToggleRow
            icon={<Monitor className="h-4 w-4 text-primary-500" />}
            label="In-app notification"
            description="Show a notification in the app when analysis finishes"
            checked={prefs.analysisInApp}
            onChange={(v) => updatePreference('analysisInApp', v)}
            disabled={saving}
          />

          {/* Analysis Complete — Email */}
          <ToggleRow
            icon={<Mail className="h-4 w-4 text-primary-500" />}
            label="Email notification"
            description="Receive an email when analysis completes or fails"
            checked={prefs.analysisEmail}
            onChange={(v) => updatePreference('analysisEmail', v)}
            disabled={saving}
          />

          {/* Analysis Complete — SMS */}
          <ToggleRow
            icon={<MessageSquare className="h-4 w-4 text-primary-500" />}
            label="SMS notification"
            description="Receive a text message when analysis completes"
            checked={prefs.analysisSms}
            onChange={(v) => updatePreference('analysisSms', v)}
            disabled={saving}
          />

          {/* Phone number input — shown when SMS is enabled */}
          {prefs.analysisSms && (
            <div className="ml-7 p-3 bg-medical-50 dark:bg-medical-700 rounded-lg">
              <label className="block text-xs font-medium text-medical-700 dark:text-medical-300 mb-1.5">
                Mobile number for SMS notifications
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={diallingCode}
                  onChange={(e) => setDiallingCode(e.target.value)}
                  className="text-sm px-2 py-1.5 border border-medical-300 dark:border-medical-600 rounded-md bg-white dark:bg-medical-800 text-medical-900 dark:text-medical-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-36 flex-shrink-0"
                >
                  {countryCodes.map((c) => (
                    <option key={`${c.country}-${c.code}`} value={c.code}>
                      {flagEmoji(c.country)} {c.country} ({c.code})
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  placeholder="7700 900000"
                  className="flex-1 text-sm px-3 py-1.5 border border-medical-300 dark:border-medical-600 rounded-md bg-white dark:bg-medical-800 text-medical-900 dark:text-medical-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  onClick={savePhone}
                  disabled={phoneSaving || getFullPhone() === (prefs.smsPhoneNumber || '')}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50"
                >
                  {phoneSaving ? 'Saving...' : getFullPhone() === (prefs.smsPhoneNumber || '') ? 'Saved' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {saving && (
        <p className="text-xs text-medical-500 dark:text-medical-400 mt-3 flex items-center space-x-1">
          <Loader className="h-3 w-3 animate-spin" />
          <span>Saving...</span>
        </p>
      )}
    </div>
  );
};

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ icon, label, description, checked, onChange, disabled }) => (
  <div className="flex items-center justify-between p-3 bg-medical-50 dark:bg-medical-700 rounded-lg">
    <div className="flex items-center space-x-3">
      {icon}
      <div>
        <p className="text-sm font-medium text-medical-800 dark:text-medical-200">{label}</p>
        <p className="text-xs text-medical-500 dark:text-medical-400">{description}</p>
      </div>
    </div>
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
        checked ? 'bg-primary-600' : 'bg-medical-300 dark:bg-medical-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);
